'use client';

import React, { useCallback } from 'react';
import { CheckCircle2, ListTodo, Users, CloudSnow, TrendingUp, Clock, Gauge } from 'lucide-react';
import Link from 'next/link';
import { usePolling } from '@/lib/usePolling';
import type { KanbanTask } from '@/lib/types';
import {
    resolveWeeklyStaffingDemand,
    type CapacityData,
} from '@/components/dashboard/WeeklyCapacityWidget';

// ─── Types ───

interface WeatherCurrent {
    risk: { level: string };
    temperature: number;
}

interface MetricRow {
    kpi_slug: string;
    value: number;
}

// ─── Risk badge colors ───

const RISK_COLORS: Record<string, { bg: string; text: string }> = {
    NONE: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
    FROST_WATCH: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
    BLACK_ICE: { bg: 'bg-rose-500/10', text: 'text-rose-400' },
    FREEZING_FOG: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
    FREEZING_RAIN: { bg: 'bg-rose-500/10', text: 'text-rose-400' },
    ICE_RISK: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
    SNOW_ACTIVE: { bg: 'bg-cyan-500/10', text: 'text-cyan-400' },
};

const RISK_DEFAULT = { bg: 'bg-zinc-500/10', text: 'text-zinc-400' };

const RISK_LABELS: Record<string, string> = {
    NONE: 'ALL CLEAR',
    FROST_WATCH: 'FROST WATCH',
    BLACK_ICE: 'BLACK ICE',
    FREEZING_FOG: 'FREEZING FOG',
    FREEZING_RAIN: 'FREEZING RAIN',
    ICE_RISK: 'ICE RISK',
    SNOW_ACTIVE: 'SNOW ACTIVE',
};

// ─── Individual KPI pill ───

const RibbonKpi = React.memo(function RibbonKpi({
    icon: Icon,
    label,
    value,
    suffix,
    accent = 'text-white',
    title,
}: {
    icon: React.ElementType;
    label: string;
    value: string | number;
    suffix?: string;
    accent?: string;
    title?: string;
}) {
    return (
        <div title={title} className="flex items-center gap-2 md:gap-3 bg-zinc-900/60 border border-white/5 rounded-xl px-3 py-2 md:px-4 md:py-3 min-w-0 md:flex-1 backdrop-blur-sm hover:border-zinc-700 transition-colors">
            <div className="p-1 md:p-1.5 rounded-lg bg-zinc-800/60">
                <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-zinc-400" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest truncate">{label}</div>
                <div className={`text-base md:text-lg font-black tracking-tight ${accent}`}>
                    {value}{suffix && <span className="text-[10px] md:text-xs font-medium text-zinc-500 ml-0.5">{suffix}</span>}
                </div>
            </div>
        </div>
    );
});

// ─── Weather badge (special styling) ───

const WeatherBadge = React.memo(function WeatherBadge({ level, temp }: { level: string; temp: number | null }) {
    const colors = RISK_COLORS[level] || RISK_DEFAULT;
    const label = RISK_LABELS[level] || level;

    return (
        <div className={`flex items-center gap-2 md:gap-3 ${colors.bg} border border-white/5 rounded-xl px-3 py-2 md:px-4 md:py-3 min-w-0 md:flex-1 backdrop-blur-sm hover:border-zinc-700 transition-colors`}>
            <div className="p-1 md:p-1.5 rounded-lg bg-zinc-800/60">
                <CloudSnow className={`w-3.5 h-3.5 md:w-4 md:h-4 ${colors.text}`} />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest truncate">Weather</div>
                <div className={`text-xs md:text-sm font-black tracking-tight ${colors.text} truncate`}>
                    {label}
                    {temp != null && <span className="text-zinc-400 font-mono text-[10px] md:text-xs ml-1 md:ml-1.5">{Math.round(temp)}°F</span>}
                </div>
            </div>
        </div>
    );
});

// ─── Main Ribbon ───

export function KpiRibbon() {
    // Task counts
    const { data: taskData } = usePolling<KanbanTask[]>({
        url: '/api/tasks',
        intervalMs: 10000,
        transform: useCallback((json: Record<string, any>) => json.tasks, []),
        initialValue: [],
    });

    // Weather
    const { data: weather } = usePolling<WeatherCurrent | null>({
        url: '/api/weather?type=current',
        intervalMs: 60000,
        transform: useCallback((json: Record<string, any>) => json.current, []),
        initialValue: null,
    });

    // Metrics (for lead velocity)
    const { data: metrics } = usePolling<MetricRow[]>({
        url: '/api/metrics',
        intervalMs: 30000,
        transform: useCallback((json: any) => Array.isArray(json) ? json : Array.isArray(json?.metrics) ? json.metrics : [], []),
        initialValue: [],
    });

    // Weekly capacity (for booked hours + available capacity)
    const { data: capacity } = usePolling<CapacityData>({
        url: '/api/admin/capacity/weekly',
        intervalMs: 120_000,
        initialValue: {
            total_capacity_minutes: 0,
            total_booked_minutes: 0,
            total_available_minutes: 0,
            staffing_expected_minutes: 0,
            staffing_p80_minutes: 0,
            staffing_crew_day_expected_minutes: 0,
            staffing_crew_day_p80_minutes: 0,
            staffing_gusto_calibration_complete: false,
            staffing_gusto_calibration_applied: false,
            staffing_gusto_calibration_factor: null,
            staffing_gusto_sample_weeks: 0,
            staffing_gusto_average_minutes: null,
            staffing_gusto_average_booked_minutes: null,
            staffing_gusto_adjustment_minutes: 0,
            staffing_gusto_window_start: null,
            staffing_gusto_window_end: null,
            staffing_non_booked_minutes: 0,
            staffing_service_capacity_minutes: 0,
            staffing_peak_planned_people: 0,
            staffing_projected_crew_days: 0,
            staffing_projected_work_days: 0,
            staffing_scheduled_crew_days: 0,
            staffing_scheduled_work_days: 0,
            staffing_projection_census_stable: false,
            staffing_projection_complete: false,
            active_employees: 0,
            crews: [],
            week_start: '',
            week_end: '',
        },
    });

    // Compute task counts
    const todoCount = taskData.filter(t => t.status === 'todo').length;
    const inProgressCount = taskData.filter(t => t.status === 'in_progress').length;
    const doneToday = taskData.filter(t => t.status === 'done').length;
    const openTasks = todoCount + inProgressCount;

    // Lead velocity from metrics
    const leadMetric = Array.isArray(metrics) ? metrics.find(m => m.kpi_slug === 'lead_velocity') : null;
    const leadVelocity = leadMetric?.value ?? 0;

    // Staffing KPIs — crew-day paid man-hours with price-budget fallback.
    const resolvedDemand = resolveWeeklyStaffingDemand(capacity);
    const hasStaffingProjection = resolvedDemand.usesCrewDayProjection;
    const staffingMinutes = resolvedDemand.minutes;
    const staffingHours = Math.round(staffingMinutes / 60);
    const bookedWorkCapacityMinutes = capacity.staffing_service_capacity_minutes > 0
        ? capacity.staffing_service_capacity_minutes
        : Math.max(0, capacity.total_capacity_minutes - capacity.staffing_non_booked_minutes);
    const availableHours = Math.round((bookedWorkCapacityMinutes - staffingMinutes) / 60);
    const isOverbooked = availableHours < 0;

    return (
        <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:gap-3 mb-6">
            <RibbonKpi
                icon={ListTodo}
                label="Tasks"
                value={openTasks}
                accent={openTasks > 10 ? 'text-amber-400' : 'text-white'}
            />
            <RibbonKpi
                icon={CheckCircle2}
                label="Done"
                value={doneToday}
                accent="text-emerald-400"
            />
            <RibbonKpi
                icon={Users}
                label="Active"
                value={inProgressCount}
                accent={inProgressCount > 0 ? 'text-cyan-400' : 'text-zinc-400'}
            />
            <RibbonKpi
                icon={TrendingUp}
                label="Leads"
                value={leadVelocity}
                suffix="/day"
                accent={leadVelocity > 0 ? 'text-white' : 'text-zinc-500'}
            />
            <RibbonKpi
                icon={Clock}
                label={hasStaffingProjection ? 'Booked Hrs' : 'Price Budget'}
                value={staffingHours}
                suffix="hrs"
                accent={staffingHours > 0 ? 'text-cyan-400' : 'text-zinc-400'}
                title={hasStaffingProjection
                    ? capacity.staffing_gusto_calibration_applied
                        ? `Booked-work forecast calibrated from ${capacity.staffing_gusto_sample_weeks} complete Gusto weeks after subtracting ${Math.round(capacity.staffing_non_booked_minutes / 60)} recurring other-work hours`
                        : 'Crew-day projected booked-work hours'
                    : 'Price-derived booking budget; crew-day forecast is incomplete'}
            />
            <RibbonKpi
                icon={Gauge}
                label={hasStaffingProjection ? 'Field Hrs Left' : 'Staffing Status'}
                value={hasStaffingProjection
                    ? isOverbooked ? `−${Math.abs(availableHours)}` : availableHours
                    : 'Pending'}
                suffix={hasStaffingProjection ? 'hrs' : undefined}
                accent={hasStaffingProjection
                    ? isOverbooked ? 'text-rose-400' : availableHours < 20 ? 'text-amber-400' : 'text-emerald-400'
                    : 'text-amber-400'}
                title={hasStaffingProjection
                    ? `Booked-work capacity after ${Math.round(capacity.staffing_non_booked_minutes / 60)} regular hours committed to other work`
                    : 'Crew-day forecast is incomplete; no labor or overtime conclusion is shown'}
            />
            <Link href="/snow-ops">
                <WeatherBadge
                    level={weather?.risk?.level ?? 'NONE'}
                    temp={weather?.temperature ?? null}
                />
            </Link>
        </div>
    );
}
